import { P, Wrapper } from '@farewill/ui'
import { Link } from 'react-router-dom'
import { FONT, GTR } from '@farewill/ui/tokens'
import styled from 'styled-components'
import omit from 'lodash/omit'
import isObject from 'lodash/isObject'
import isEmpty from 'lodash/isEmpty'

import AdminIcon from 'components/admin-icon'
import { ActivityIcon } from 'components/activity/activity-icon'
import ActivityTimestamp from 'components/activity-timestamp'

import { Event as EventType } from 'lib/models/event'
import { formatToHuman } from 'utils/helpers'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.L};
`

const StyledDocumentLink = styled(Wrapper)`
  display: block;
  font-size: ${FONT.SIZE.S};
`

const getToFromValue = (value: string | Record<string, string>): string => {
  if (isObject(value)) {
    /**
     * If the to/from objects are objects, we don't need to print the
     * id, we can just print the other values in the object. There
     * will likely only be one other value in the object, like the
     * package name.
     */
    return Object.entries(omit(value, 'id'))
      .map(([_key, value]) => value)
      .join(', ')
  }

  return value
}

const formatMetadataValue = (value: string | string[] | object): string => {
  if (Array.isArray(value)) {
    const formattedStrings = value.map((string) => {
      return formatToHuman(string)
    })
    return formattedStrings.join(', ')
  }

  if (typeof value === 'object') {
    return ''
  }

  return formatToHuman(value)
}

const DOCUMENT_EVENT_NAMES = [
  'probate_document_uploaded',
  'probate_document_saved_to_case',
  'probate_document_deleted',
] as const

const getDocumentEventLabel = (name: string, fileName?: string): string => {
  switch (name) {
    case 'probate_document_uploaded':
      return ` uploaded a document`
    case 'probate_document_saved_to_case':
      return ` saved a document to the case`
    case 'probate_document_deleted':
      return ` deleted a document`
    default:
      return formatToHuman(name)
  }
}

const Event = ({ event }: { event: EventType }): React.ReactElement => {
  const { createdAt, adminUser, name, metadata } = event.attributes
  const additionalMetadata = omit(metadata, [
    'to',
    'from',
    'documentId',
    'fileName',
    'isDeleted',
    'uploadedByRole',
    'documents',
  ])
  const isDocumentEvent = DOCUMENT_EVENT_NAMES.includes(
    name as (typeof DOCUMENT_EVENT_NAMES)[number]
  )

  if (isDocumentEvent) {
    return (
      <StyledWrapper>
        <ActivityIcon type="manage_document" />
        <Wrapper>
          <P margin={0} size="S">
            <strong>
              {metadata?.uploadedByRole === 'customer'
                ? 'Customer'
                : adminUser.name}
            </strong>
            {getDocumentEventLabel(name, metadata?.fileName as string)}
          </P>
          <ActivityTimestamp datetime={createdAt} />
          <StyledDocumentLink>
            {!metadata.isDeleted ? (
              <Link
                to={`/probate/cases/${event.attributes.probateCaseId}/documents/${metadata?.documentId}`}
              >
                {metadata.fileName}
              </Link>
            ) : (
              metadata.fileName
            )}
          </StyledDocumentLink>
        </Wrapper>
      </StyledWrapper>
    )
  }

  return (
    <StyledWrapper>
      <AdminIcon user={adminUser} size="L" />
      <Wrapper>
        <P margin={0} size="S">
          {formatToHuman(name)}{' '}
          {metadata?.from && <>from {getToFromValue(metadata.from)} </>}
          {metadata?.to && (
            <>
              to <strong>{getToFromValue(metadata.to)}</strong>{' '}
            </>
          )}
          by <strong>{adminUser.name}</strong>
        </P>
        {!isEmpty(additionalMetadata) && (
          <P margin={0} size="S">
            {Object.entries(additionalMetadata).map(([key, value], index) => (
              <span key={key}>
                {index > 0 && ', '}
                {formatToHuman(key)}:{' '}
                {/* We can assume all "notes" where the value typeof is 'string' are already in the format in which we want them displayed */}
                {key === 'notes' && typeof value === 'string'
                  ? value
                  : formatMetadataValue(value)}
              </span>
            ))}
          </P>
        )}
        <ActivityTimestamp datetime={createdAt} />
      </Wrapper>
    </StyledWrapper>
  )
}

export default Event
